/**
 * Organizasyon ağacı — kurum → bölüm → departman → birim → ekip.
 *
 * Kütüphane büyüdükçe düz bir persona listesi yönetilemez hâle geliyor;
 * bu ağaç personaları gerçek bir organizasyon şemasına yerleştirir ve
 * "Hukuk Departmanı ile tartış" gibi bir kadro seçimini tek tıkla mümkün
 * kılar (alt birimler dahil).
 *
 * Etkileşim notları:
 *   • Seçim FİLTREdir, alt ağacı da kapsar — bir departmanı seçmek
 *     altındaki ekiplerin personalarını da listeler. Kullanıcının beklentisi
 *     budur; yalnız-doğrudan davranışı için "0 / 14" sayacı ipucu verir.
 *   • Silme varsayılan olarak alt birimleri ebeveyne TERFİ ettirir. Bir
 *     klasörü silmek altındaki organizasyonu sessizce yok edemez.
 *   • Sürükle-bırak yerine açık "Taşı" seçici kullanıldı: dokunmatik ve
 *     klavye erişilebilirliği bedava geliyor, 5 seviyeli iç içe listede
 *     drop hedefi belirsizliği de ortadan kalkıyor.
 */
import { useMemo, useState } from "react";
import {
  UNIT_KIND_ICONS,
  UNIT_KIND_LABELS,
  unitChildren,
  type PersonaUnit,
  type UnitKind,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  createUnit,
  deleteUnit,
  updateUnit,
} from "../../transport/personaLibraryClient";
import { Btn, ErrorBanner, Field, Modal, Select, TextArea, TextInput } from "./ui";

export type UnitSelection = { kind: "all" } | { kind: "unassigned" } | { kind: "unit"; id: string };

function Row({
  unit,
  units,
  selection,
  onSelect,
  onAddChild,
  onEdit,
  onDelete,
  busy,
}: {
  unit: PersonaUnit;
  units: PersonaUnit[];
  selection: UnitSelection;
  onSelect: (sel: UnitSelection) => void;
  onAddChild: (parent: PersonaUnit) => void;
  onEdit: (unit: PersonaUnit) => void;
  onDelete: (unit: PersonaUnit) => void;
  busy: boolean;
}) {
  const [open, setOpen] = useState(true);
  const children = useMemo(() => unitChildren(units, unit.id), [units, unit.id]);
  const active = selection.kind === "unit" && selection.id === unit.id;

  return (
    <li>
      <div
        className={[
          "group flex items-start gap-1 rounded-lg pr-1 transition-colors",
          active ? "bg-violet-500/15" : "hover:bg-white/[0.04]",
        ].join(" ")}
        // Girinti 8px: derin adlar ("Silah Sistemleri ve Mühimmat Sektör
        // Müdürlüğü") 5. seviyede bile okunur genişlikte kalsın.
        style={{ paddingLeft: unit.depth * 8 }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={children.length === 0}
          aria-label={open ? "Daralt" : "Genişlet"}
          className="mt-1.5 w-3.5 shrink-0 text-[10px] text-slate-600 disabled:opacity-0"
        >
          {children.length > 0 ? (open ? "▾" : "▸") : ""}
        </button>

        <button
          type="button"
          onClick={() => onSelect({ kind: "unit", id: unit.id })}
          className="flex min-w-0 flex-1 items-start gap-1.5 py-1.5 text-left"
          // Tam ad + tür + sayım tooltip'te: kırpılma olsa bile bilgi
          // erişilebilir kalır.
          title={`${unit.name} — ${UNIT_KIND_LABELS[unit.kind]}${
            unit.description ? `\n${unit.description}` : ""
          }\nDoğrudan ${unit.personaCount} · alt ağaç dahil ${unit.totalCount} persona`}
        >
          <span className="mt-px shrink-0 text-[11px]" aria-hidden>
            {UNIT_KIND_ICONS[unit.kind] ?? "📁"}
          </span>
          {/* `truncate` yerine iki satıra sarma: uzun kurum/müdürlük adları
              "Silah Siste…" olarak kesiliyordu ve hangi birim olduğu
              anlaşılmıyordu. */}
          <span
            className={[
              "min-w-0 flex-1 break-words text-[12.5px] leading-snug line-clamp-2",
              active ? "font-medium text-white" : "text-slate-300",
            ].join(" ")}
          >
            {unit.name}
          </span>
          <span
            className="mt-px shrink-0 text-[10px] tabular-nums text-slate-600"
            title={`Doğrudan ${unit.personaCount} · alt ağaç dahil ${unit.totalCount}`}
          >
            {unit.personaCount}
            {unit.totalCount !== unit.personaCount && (
              <span className="text-slate-700">/{unit.totalCount}</span>
            )}
          </span>
        </button>

        <span className="mt-1 flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={() => onAddChild(unit)}
            disabled={busy || unit.depth >= 4}
            title={unit.depth >= 4 ? "En fazla 5 seviye" : "Alt birim ekle"}
            className="rounded px-1 text-[12px] text-slate-500 hover:text-violet-300 disabled:opacity-30"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => onEdit(unit)}
            disabled={busy}
            title="Düzenle"
            className="rounded px-1 text-[10px] text-slate-500 hover:text-white disabled:opacity-30"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={() => onDelete(unit)}
            disabled={busy}
            title="Sil"
            className="rounded px-1 text-[10px] text-slate-500 hover:text-rose-400 disabled:opacity-30"
          >
            ✕
          </button>
        </span>
      </div>

      {open && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <Row
              key={child.id}
              unit={child}
              units={units}
              selection={selection}
              onSelect={onSelect}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              busy={busy}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function UnitTree({
  units,
  unassignedCount,
  totalCount,
  selection,
  onSelect,
  onChanged,
}: {
  units: PersonaUnit[];
  unassignedCount: number;
  totalCount: number;
  selection: UnitSelection;
  onSelect: (sel: UnitSelection) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PersonaUnit | null>(null);
  const [creatingUnder, setCreatingUnder] = useState<PersonaUnit | null | "root">(null);

  const roots = useMemo(() => unitChildren(units, null), [units]);

  async function handleDelete(unit: PersonaUnit) {
    const hasChildren = units.some((u) => u.parentId === unit.id);
    const message = hasChildren
      ? `"${unit.name}" silinsin mi?\n\nAlt birimleri bir üst seviyeye taşınacak. ` +
        `İçindeki ${unit.personaCount} persona silinmez, "Atanmamış" listesine düşer.`
      : `"${unit.name}" silinsin mi?\n\nİçindeki ${unit.personaCount} persona silinmez, ` +
        `"Atanmamış" listesine düşer.`;
    if (!confirm(message)) return;
    setBusy(true);
    try {
      await deleteUnit(unit.id, false);
      if (selection.kind === "unit" && selection.id === unit.id) {
        onSelect({ kind: "all" });
      }
      onChanged();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Birim silinemedi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="flex w-full flex-col gap-2 rounded-xl border border-white/[0.07] bg-white/[0.02] p-2.5">
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Organizasyon
        </span>
        <Btn size="sm" tone="ghost" onClick={() => setCreatingUnder("root")} disabled={busy}>
          + Kurum
        </Btn>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <ul className="space-y-0.5">
        <li>
          <button
            type="button"
            onClick={() => onSelect({ kind: "all" })}
            className={[
              "flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left transition-colors",
              selection.kind === "all"
                ? "bg-violet-500/15 font-medium text-white"
                : "text-slate-300 hover:bg-white/[0.04]",
            ].join(" ")}
          >
            <span className="text-[11px]" aria-hidden>
              ◆
            </span>
            <span className="flex-1 text-[12.5px]">Tüm personalar</span>
            <span className="text-[10px] tabular-nums text-slate-600">{totalCount}</span>
          </button>
        </li>

        {roots.map((unit) => (
          <Row
            key={unit.id}
            unit={unit}
            units={units}
            selection={selection}
            onSelect={onSelect}
            onAddChild={(parent) => setCreatingUnder(parent)}
            onEdit={setEditing}
            onDelete={handleDelete}
            busy={busy}
          />
        ))}

        {unassignedCount > 0 && (
          <li>
            <button
              type="button"
              onClick={() => onSelect({ kind: "unassigned" })}
              className={[
                "flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left transition-colors",
                selection.kind === "unassigned"
                  ? "bg-violet-500/15 font-medium text-white"
                  : "text-slate-400 hover:bg-white/[0.04]",
              ].join(" ")}
            >
              <span className="text-[11px]" aria-hidden>
                ○
              </span>
              <span className="flex-1 text-[12.5px]">Atanmamış</span>
              <span className="text-[10px] tabular-nums text-slate-600">
                {unassignedCount}
              </span>
            </button>
          </li>
        )}
      </ul>

      {roots.length === 0 && (
        <p className="px-2 py-3 text-[11px] leading-relaxed text-slate-600">
          Henüz organizasyon yok. Bir kurum oluşturup altına departman ve birimler
          ekleyerek personalarını gerçek bir şemaya yerleştirebilirsin.
        </p>
      )}

      <UnitFormModal
        open={creatingUnder !== null || editing !== null}
        unit={editing}
        parent={creatingUnder === "root" ? null : creatingUnder}
        units={units}
        onClose={() => {
          setCreatingUnder(null);
          setEditing(null);
        }}
        onSaved={() => {
          setCreatingUnder(null);
          setEditing(null);
          onChanged();
        }}
      />
    </aside>
  );
}

function UnitFormModal({
  open,
  unit,
  parent,
  units,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Dolu ⇒ düzenleme, boş ⇒ oluşturma. */
  unit: PersonaUnit | null;
  parent: PersonaUnit | null;
  units: PersonaUnit[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = unit !== null;
  const [name, setName] = useState("");
  const [kind, setKind] = useState<UnitKind>("unit");
  const [description, setDescription] = useState("");
  const [mission, setMission] = useState("");
  const [parentId, setParentId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initFor, setInitFor] = useState<string | null>(null);

  // Modal her açılışta hedefe göre sıfırlanmalı; `useEffect` yerine render
  // sırasında tek seferlik senkronizasyon, açılış anında yanlış değerin bir
  // kare boyunca görünmesini önlüyor.
  const key = `${unit?.id ?? "new"}:${parent?.id ?? "root"}:${open}`;
  if (open && initFor !== key) {
    setInitFor(key);
    setName(unit?.name ?? "");
    setKind(unit?.kind ?? defaultKindForDepth(parent ? parent.depth + 1 : 0));
    setDescription(unit?.description ?? "");
    setMission(unit?.mission ?? "");
    setParentId(unit ? (unit.parentId ?? "") : (parent?.id ?? ""));
    setError(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (isEdit && unit) {
        await updateUnit(unit.id, {
          name: name.trim(),
          kind,
          description: description.trim(),
          mission: mission.trim(),
          parentId: parentId || null,
        });
      } else {
        await createUnit({
          name: name.trim(),
          kind,
          description: description.trim(),
          mission: mission.trim(),
          parentId: parentId || null,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  // Kendini ve torunlarını ebeveyn olarak seçtirme — döngü oluşurdu.
  const validParents = units.filter((u) => {
    if (!unit) return u.depth < 4;
    if (u.id === unit.id) return false;
    if ((u.path || "").startsWith(`${unit.path}/`)) return false;
    return u.depth < 4;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Birimi Düzenle" : parent ? `${parent.name} altına ekle` : "Yeni Kurum"}
      subtitle="Personalarını gerçek bir organizasyon şemasına yerleştir; tartışma başlatırken bir birimi bütün olarak masaya çağırabilirsin."
      width="max-w-lg"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </Btn>
          <Btn tone="primary" onClick={save} loading={busy} disabled={name.trim().length < 2}>
            {isEdit ? "Kaydet" : "Oluştur"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <Field label="Ad" required>
          <TextInput
            value={name}
            autoFocus
            maxLength={100}
            placeholder="ör. Hukuk ve Uyum Departmanı"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tür">
            <Select value={kind} onChange={(e) => setKind(e.target.value as UnitKind)}>
              {(Object.keys(UNIT_KIND_LABELS) as UnitKind[]).map((k) => (
                <option key={k} value={k}>
                  {UNIT_KIND_ICONS[k]} {UNIT_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Üst Birim" hint="Boş bırakılırsa kök seviyede durur.">
            <Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— Kök —</option>
              {validParents.map((u) => (
                <option key={u.id} value={u.id}>
                  {"\u00A0".repeat(u.depth * 2)}
                  {u.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Açıklama">
          <TextInput
            value={description}
            maxLength={600}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field
          label="Birim Misyonu"
          hint="Opsiyonel. Bu birimin hangi kararlardan sorumlu olduğunu not et."
        >
          <TextArea
            rows={2}
            value={mission}
            maxLength={1200}
            onChange={(e) => setMission(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

function defaultKindForDepth(depth: number): UnitKind {
  const ladder: UnitKind[] = [
    "organization",
    "division",
    "department",
    "unit",
    "team-cell",
  ];
  return ladder[Math.min(depth, ladder.length - 1)] ?? "unit";
}
